import * as v from 'valibot'
import {
  IndicationType,
  icd10CodeSchema,
  timePeriodSchema,
  type Indication,
} from '../types'
import { canonicalClone, canonicalJson, compareCodeUnits } from './canonical'
import {
  CONFIGURATION_OPERATION_SCHEMA_VERSION,
  ConfigurationDomainError,
  type ConfigurationOperation,
} from './types'

const operationKinds = new Set<ConfigurationOperation['kind']>([
  'set_indication_details',
  'set_hcpcs_codes',
  'set_age_rule',
  'set_dosing_rule',
  'set_frequency_rule',
  'set_max_daily_units_rule',
  'set_max_units_over_time_rule',
  'upsert_diagnosis_code',
  'remove_diagnosis_code',
])

const hasInvalidPositiveField = (value: Record<string, unknown>): boolean =>
  Object.values(value).some(
    (field) =>
      typeof field === 'number' && (!Number.isFinite(field) || field <= 0),
  )

const actorSchema = v.strictObject({
  id: v.pipe(v.string(), v.nonEmpty()),
  displayName: v.optional(v.string()),
})
const provenanceSchema = v.strictObject({
  source: v.optional(v.string()),
  sourceId: v.optional(v.string()),
  note: v.optional(v.string()),
})
const lineageSchema = v.strictObject({
  operationIds: v.pipe(v.array(v.pipe(v.string(), v.nonEmpty())), v.nonEmpty()),
})
const envelopeEntries = {
  id: v.pipe(v.string(), v.nonEmpty()),
  schemaVersion: v.literal(CONFIGURATION_OPERATION_SCHEMA_VERSION),
  indicationId: v.pipe(v.string(), v.nonEmpty()),
  /*
    Optional, not required-but-blankable: a preview compiles the effective
    value of a change before the reviewer has written a rationale for it, so
    the operation can't demand one yet. Apply requires it client-side instead.
  */
  rationale: v.optional(v.pipe(v.string(), v.trim(), v.nonEmpty())),
  provenance: v.optional(provenanceSchema),
  actor: actorSchema,
  createdAt: v.pipe(v.string(), v.nonEmpty()),
  lineage: v.optional(lineageSchema),
}
const nullableString = v.optional(v.nullable(v.string()))
const nullableNumber = v.optional(v.nullable(v.number()))
const nullableTimePeriod = v.optional(v.nullable(timePeriodSchema))
const positiveNumber = v.pipe(
  v.number(),
  v.check((value) => value > 0, 'Value must be positive'),
)

export const configurationOperationSchema = v.variant('kind', [
  v.strictObject({
    ...envelopeEntries,
    kind: v.literal('set_indication_details'),
    /*
      Every field stays validated — including the ones WFL-7734 removed from
      `IndicationDetailsValue` — because operations authored before that
      narrowing still carry them and must keep parsing and replaying exactly.
      `strictObject` (not `looseObject`) matters here: it is what stops a
      `set_indication_details` operation from smuggling an arbitrary key (e.g.
      `maxDailyHcpcsUnits`) past its own group's validation, since
      `applyConfigurationOperation` patches every key in `value` onto the
      indication unchecked. New operations only ever set `description`; all
      the other fields being optional is what lets that narrower shape pass
      the same schema as a legacy 7-field one.
    */
    value: v.strictObject({
      name: v.optional(v.pipe(v.string(), v.nonEmpty())),
      description: nullableString,
      description_json: v.optional(v.nullable(v.unknown())),
      type: v.optional(v.nullable(v.enum(IndicationType))),
      citations: v.optional(v.nullable(v.record(v.string(), v.unknown()))),
      sourceDocumentId: nullableString,
      sourcePageNumber: nullableNumber,
    }),
  }),
  v.strictObject({
    ...envelopeEntries,
    kind: v.literal('set_hcpcs_codes'),
    value: v.array(v.string()),
  }),
  v.strictObject({
    ...envelopeEntries,
    kind: v.literal('set_age_rule'),
    value: v.strictObject({
      age: nullableString,
      ageMin: nullableNumber,
      ageMinUom: nullableTimePeriod,
      ageMax: nullableNumber,
      ageMaxUom: nullableTimePeriod,
    }),
  }),
  v.strictObject({
    ...envelopeEntries,
    kind: v.literal('set_dosing_rule'),
    value: v.strictObject({
      dosingSummary: nullableString,
      dosingSummary_json: v.optional(v.nullable(v.unknown())),
      patientAttribute: nullableString,
      patientAttributeValue: nullableString,
    }),
  }),
  v.strictObject({
    ...envelopeEntries,
    kind: v.literal('set_frequency_rule'),
    value: v.strictObject({
      frequencyMaxAdminDays: nullableNumber,
      frequencyMaxFrequency: nullableNumber,
      frequencyTimePeriod: nullableTimePeriod,
    }),
  }),
  v.strictObject({
    ...envelopeEntries,
    kind: v.literal('set_max_daily_units_rule'),
    value: v.nullable(positiveNumber),
  }),
  v.strictObject({
    ...envelopeEntries,
    kind: v.literal('set_max_units_over_time_rule'),
    value: v.strictObject({
      maxUnitsOverTimeMaxHCPCSUnits: nullableNumber,
      maxUnitsOverHcpcsMaxFrequency: nullableNumber,
      maxUnitsOverHcpcsTimePeriod: nullableTimePeriod,
    }),
  }),
  v.strictObject({
    ...envelopeEntries,
    kind: v.literal('upsert_diagnosis_code'),
    value: v.strictObject(icd10CodeSchema.entries),
  }),
  v.strictObject({
    ...envelopeEntries,
    kind: v.literal('remove_diagnosis_code'),
    value: v.strictObject({ code: v.pipe(v.string(), v.nonEmpty()) }),
  }),
])

const diagnosisTarget = (indicationId: string, code: string): string =>
  `indication:${indicationId}:diagnosis:${code.trim().toUpperCase()}`

export const operationSemanticTarget = (
  operation: ConfigurationOperation,
): string => {
  switch (operation.kind) {
    case 'upsert_diagnosis_code':
      return diagnosisTarget(operation.indicationId, operation.value.code)
    case 'remove_diagnosis_code':
      return diagnosisTarget(operation.indicationId, operation.value.code)
    default:
      return `indication:${operation.indicationId}:${operation.kind}`
  }
}

export const operationFieldValue = (
  indication: Indication,
  field: string,
): unknown => {
  if (field.startsWith('icd10Codes.')) {
    const code = field.slice('icd10Codes.'.length).toUpperCase()
    return indication.icd10Codes?.find(
      (item) => item.code.toUpperCase() === code,
    )
  }
  return indication[field as keyof Indication]
}

const requireEnvelope = (
  operation: ConfigurationOperation,
): ConfigurationOperation => {
  if (operation.schemaVersion !== CONFIGURATION_OPERATION_SCHEMA_VERSION) {
    throw new ConfigurationDomainError(
      'UNSUPPORTED_OPERATION_VERSION',
      `Unsupported configuration operation schema ${operation.schemaVersion}`,
      { operationId: operation.id },
    )
  }
  if (
    !operationKinds.has(operation.kind) ||
    !operation.id?.trim() ||
    !operation.indicationId?.trim() ||
    !operation.actor?.id?.trim() ||
    !operation.createdAt ||
    Number.isNaN(Date.parse(operation.createdAt))
  ) {
    throw new ConfigurationDomainError(
      'INVALID_OPERATION',
      `Configuration operation ${operation.id || '<unknown>'} is invalid`,
      { operationId: operation.id },
    )
  }
  const result = v.safeParse(configurationOperationSchema, operation)
  if (!result.success) {
    throw new ConfigurationDomainError(
      'INVALID_OPERATION',
      `Configuration operation ${operation.id} does not match its schema`,
      { operationId: operation.id, issues: result.issues },
    )
  }
  if (
    typeof result.output.value === 'object' &&
    result.output.value !== null &&
    !Array.isArray(result.output.value) &&
    Object.keys(result.output.value).length === 0
  ) {
    throw new ConfigurationDomainError(
      'INVALID_OPERATION',
      `Configuration operation ${operation.id} has no changes`,
      { operationId: operation.id },
    )
  }
  return result.output as ConfigurationOperation
}

const normalizeOperationValue = (
  operation: ConfigurationOperation,
): ConfigurationOperation => {
  switch (operation.kind) {
    case 'set_indication_details': {
      /*
        `sourcePageNumber` is validated here rather than in the schema because
        `IndicationDetailsValue` no longer types it (WFL-7734) even though the
        schema above still parses it for legacy operations.
      */
      const sourcePageNumber = (
        operation.value as { sourcePageNumber?: number | null }
      ).sourcePageNumber
      if (
        sourcePageNumber !== undefined &&
        sourcePageNumber !== null &&
        sourcePageNumber <= 0
      ) {
        break
      }
      return operation
    }
    case 'set_hcpcs_codes':
      if (!Array.isArray(operation.value)) break
      return {
        ...operation,
        value: [
          ...new Set(
            operation.value
              .map((code) => code.trim().toUpperCase())
              .filter(Boolean),
          ),
        ].sort(),
      }
    case 'upsert_diagnosis_code': {
      const result = v.safeParse(icd10CodeSchema, operation.value)
      if (!result.success || !operation.value.code.trim()) break
      if (
        (operation.value.isRemoved &&
          operation.value.acceptanceState === 'included') ||
        (operation.value.acceptanceState !== 'excluded' &&
          operation.value.exclusionNote)
      ) {
        throw new ConfigurationDomainError(
          'OPERATION_CONFLICT',
          `Diagnosis operation ${operation.id} contains contradictory decisions`,
          { operationId: operation.id, code: operation.value.code },
        )
      }
      return {
        ...operation,
        value: {
          ...operation.value,
          code: operation.value.code.trim().toUpperCase(),
        },
      }
    }
    case 'remove_diagnosis_code':
      if (!operation.value?.code?.trim()) break
      return {
        ...operation,
        value: { code: operation.value.code.trim().toUpperCase() },
      }
    case 'set_max_daily_units_rule':
      if (
        operation.value !== null &&
        (!Number.isFinite(operation.value) || operation.value <= 0)
      ) {
        break
      }
      return operation
    case 'set_age_rule':
    case 'set_frequency_rule':
    case 'set_max_units_over_time_rule':
      if (hasInvalidPositiveField(operation.value)) break
      return operation
    default:
      if (operation.value && typeof operation.value === 'object') {
        return operation
      }
  }

  throw new ConfigurationDomainError(
    'INVALID_OPERATION',
    `Configuration operation ${operation.id} has an invalid value`,
    { operationId: operation.id, kind: operation.kind },
  )
}

export const normalizeConfigurationOperation = (
  operation: ConfigurationOperation,
): ConfigurationOperation =>
  canonicalClone(normalizeOperationValue(requireEnvelope(operation)))

const patchKinds = new Set<ConfigurationOperation['kind']>([
  'set_indication_details',
  'set_age_rule',
  'set_dosing_rule',
  'set_frequency_rule',
  'set_max_units_over_time_rule',
])

const coalesce = (
  previous: ConfigurationOperation,
  next: ConfigurationOperation,
): ConfigurationOperation => {
  const operationIds = [
    ...new Set([
      ...(previous.lineage?.operationIds ?? []),
      previous.id,
      ...(next.lineage?.operationIds ?? []),
    ]),
  ]
  if (
    previous.kind === next.kind &&
    patchKinds.has(next.kind) &&
    typeof previous.value === 'object' &&
    previous.value !== null &&
    typeof next.value === 'object' &&
    next.value !== null
  ) {
    return canonicalClone({
      ...next,
      value: { ...previous.value, ...next.value },
      lineage: { operationIds },
    } as ConfigurationOperation)
  }
  return canonicalClone({
    ...next,
    lineage: { operationIds },
  })
}

export const normalizeConfigurationOperations = (
  operations: ConfigurationOperation[],
): ConfigurationOperation[] => {
  const byId = new Map<string, ConfigurationOperation>()
  for (const operation of operations.map(normalizeConfigurationOperation)) {
    const duplicate = byId.get(operation.id)
    if (duplicate && canonicalJson(duplicate) !== canonicalJson(operation)) {
      throw new ConfigurationDomainError(
        'OPERATION_CONFLICT',
        `Operation ID ${operation.id} refers to different intents`,
        { operationId: operation.id },
      )
    }
    byId.set(operation.id, operation)
  }

  const ordered = [...byId.values()].sort(
    (left, right) =>
      compareCodeUnits(left.createdAt, right.createdAt) ||
      compareCodeUnits(left.id, right.id),
  )
  const byTarget = new Map<string, ConfigurationOperation>()
  for (const operation of ordered) {
    const target = operationSemanticTarget(operation)
    const previous = byTarget.get(target)
    byTarget.set(target, previous ? coalesce(previous, operation) : operation)
  }

  return [...byTarget.values()].sort((left, right) =>
    compareCodeUnits(
      operationSemanticTarget(left),
      operationSemanticTarget(right),
    ),
  )
}

const patchOptionalFields = (
  indication: Indication,
  patch: Record<string, unknown>,
): Indication => {
  const next = { ...indication } as Record<string, unknown>
  for (const [field, value] of Object.entries(patch)) {
    if (value === null) delete next[field]
    else next[field] = value
  }
  return next as unknown as Indication
}

export const applyConfigurationOperation = (
  indication: Indication,
  operation: ConfigurationOperation,
): Indication => {
  switch (operation.kind) {
    case 'set_indication_details':
    case 'set_dosing_rule':
    case 'set_frequency_rule':
    case 'set_max_units_over_time_rule':
      return patchOptionalFields(indication, operation.value)
    case 'set_age_rule':
      return { ...indication, ...operation.value }
    case 'set_hcpcs_codes':
      return { ...indication, hcpcsCodes: operation.value }
    case 'set_max_daily_units_rule':
      return patchOptionalFields(indication, {
        maxDailyHcpcsUnits: operation.value,
      })
    case 'upsert_diagnosis_code': {
      const code = operation.value.code.toUpperCase()
      const current = indication.icd10Codes ?? []
      const index = current.findIndex(
        (item) => item.code.toUpperCase() === code,
      )
      const icd10Codes = [...current]
      if (index === -1) icd10Codes.push(operation.value)
      else icd10Codes[index] = operation.value
      return {
        ...indication,
        icd10Codes: icd10Codes.sort((left, right) =>
          compareCodeUnits(left.code, right.code),
        ),
      }
    }
    case 'remove_diagnosis_code':
      return {
        ...indication,
        icd10Codes: (indication.icd10Codes ?? []).filter(
          (item) =>
            item.code.toUpperCase() !== operation.value.code.toUpperCase(),
        ),
      }
  }
}

export const operationFields = (
  operation: ConfigurationOperation,
): string[] => {
  switch (operation.kind) {
    case 'set_indication_details':
    case 'set_age_rule':
    case 'set_dosing_rule':
    case 'set_frequency_rule':
    case 'set_max_units_over_time_rule':
      return Object.keys(operation.value).sort()
    case 'set_hcpcs_codes':
      return ['hcpcsCodes']
    case 'set_max_daily_units_rule':
      return ['maxDailyHcpcsUnits']
    case 'upsert_diagnosis_code':
    case 'remove_diagnosis_code':
      return [`icd10Codes.${operation.value.code}`]
  }
}
